import { useEffect, useRef, useState } from 'react';

import { resolveUploadUrl } from '@/lib/uploads';
import { MessageBubbleList, type BubbleMessage } from '@/features/messaging/MessageBubbleList';
import { MessageComposer } from '@/features/messaging/MessageComposer';
import { useMyThread, useSupportMessages, useSendSupportMessage, useMarkSupportRead } from '../hooks/useSupport';
import { useSupportSocket } from '../hooks/useSupportSocket';
import { SupportAttachment } from './SupportAttachment';

export function SupportProPage() {
  const { data: thread, isLoading: threadLoading } = useMyThread();
  const conversationId = thread?.id;
  const { data: messagesData, isLoading: msgsLoading } = useSupportMessages(conversationId);
  const send = useSendSupportMessage();
  const markRead = useMarkSupportRead();
  const { isTyping, emitTypingStart, emitTypingStop } = useSupportSocket(conversationId);

  // Mark read on open AND whenever a new staff message arrives while the thread
  // is already open (unreadCountPro goes positive) — re-running on the count, not
  // just on conversationId, prevents a stuck badge while actively viewing.
  useEffect(() => {
    if (conversationId && thread && thread.unreadCountPro > 0) markRead.mutate(conversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, thread?.unreadCountPro]);

  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX = 10 * 1024 * 1024;
  const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
  const [fileError, setFileError] = useState<string | null>(null);
  const onPick = (list: FileList | null) => {
    if (!list) return;
    const picked = Array.from(list);
    const bad = picked.find((f) => !ALLOWED.includes(f.type) || f.size > MAX);
    if (bad) {
      setFileError('File non supportato o troppo grande (max 10MB: JPG, PNG, WebP, GIF, PDF)');
      return;
    }
    setFileError(null);
    setFiles((prev) => [...prev, ...picked]);
  };

  // Pro side: the professional's own messages sit on the "mine" (right) side;
  // staff replies show on the other side with the "Supporto" identity.
  const messages: BubbleMessage[] = (messagesData?.data ?? []).slice().reverse().map((m) => {
    const isStaff = m.senderRole === 'super_admin';
    return {
      id: m.id, content: m.content, status: m.status, createdAt: m.createdAt,
      isMine: !isStaff,
      senderName: isStaff ? m.senderDisplayName : undefined,
      senderAvatar: isStaff && m.senderAvatar ? resolveUploadUrl(m.senderAvatar) : undefined,
    };
  });

  return (
    <div className="mx-auto flex h-[calc(100vh-5rem)] max-w-3xl flex-col px-4 py-4 sm:px-6 lg:px-8">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-lg shadow-neutral-200/60">
        <div className="flex h-[3.75rem] items-center gap-3 border-b border-neutral-200 px-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 ring-2 ring-neutral-200">
            <img src="/images/logo-navy.png" alt="" className="h-5 w-5 object-contain opacity-60" />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-neutral-900">Klaro7 Support</p>
            {isTyping(conversationId ?? '') && <p className="text-[11px] font-medium text-brand-teal">sta scrivendo…</p>}
          </div>
        </div>

        <MessageBubbleList
          messages={messages}
          isLoading={threadLoading || msgsLoading}
          emptyTitle="Scrivi a Klaro7"
          emptySubtitle="Ti rispondiamo il prima possibile."
          typingLabel={isTyping(conversationId ?? '') ? 'Klaro7 Support sta scrivendo…' : null}
          renderAttachments={(id) => {
            const msg = (messagesData?.data ?? []).find((m) => m.id === id);
            if (!msg || msg.attachments.length === 0) return null;
            return (
              <div className="flex flex-col gap-1">
                {msg.attachments.map((a, i) => (
                  <SupportAttachment key={i} messageId={id} index={i} meta={a} />
                ))}
              </div>
            );
          }}
        />

        <MessageComposer
          onSend={(content) => {
            if (conversationId) {
              send.mutate({ conversationId, content, files });
              setFiles([]);
              setFileError(null);
            }
          }}
          onTyping={() => { if (conversationId) emitTypingStart(conversationId); }}
          onTypingStop={() => { if (conversationId) emitTypingStop(conversationId); }}
          placeholder="Scrivi un messaggio a Klaro7…"
          disabled={!conversationId}
          allowEmpty={files.length > 0}
          leftControl={
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => { onPick(e.target.files); e.target.value = ''; }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                aria-label="Allega file"
                className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                </svg>
              </button>
            </>
          }
          topSlot={
            (files.length > 0 || fileError) ? (
              <div className="mb-2 flex flex-col gap-1">
                {fileError && <p className="text-[11px] text-red-600">{fileError}</p>}
                <div className="flex flex-wrap gap-1">
                  {files.map((f, i) => (
                    <span key={i} className="flex items-center gap-1 rounded-lg bg-neutral-100 px-2 py-1 text-[11px] text-neutral-600">
                      {f.name}
                      <button
                        type="button"
                        onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                        aria-label="Rimuovi"
                        className="text-neutral-400 hover:text-red-500"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
}
